import { createBrowserRouter, Navigate } from "react-router";

import Profile from "@/components/Account/profile/Profile";
import IEBPage from "@/components/IEBPage/IEBPage";
import LogsDisplay from "@/components/LogsDisplay";
import Preview from "@/components/membership/Preview/Preview";
import ProcessConfirmation from "@/components/membership/ProcessConfirmation/ProcessConfirmation";
import TemplateEditor from "@/components/membership/Template/TemplateEditor";
import TemplatesTable from "@/components/membership/Template/TemplatesTable";
import FileSelector from "@/components/membership/Uploader/FileSelector";
import NotFound from "@/components/NotFound/NotFound";
import Confirmation from "@/components/political/Confirmation";
import VolunteerDashboard from "@/components/political/Dashboard/Dashboard";
import Events from "@/components/political/Events";
import EventDataTemplateEditor from "@/components/political/Template/TemplateEditor";
import PoliticalDataFileSelector from "@/components/political/Uploader/FileSelector";
import RoleWrapper from "@/components/RoleWrapper";
import UserGuide from "@/components/UserGuide/UserGuide";
import APIDocPage from "@/components/UserGuide/UserGuideComponents/APIDocPage";
import CanvaEmbed from "@/components/UserGuide/UserGuideComponents/CanvaEmbed";
import { canvaMappings } from "@/components/UserGuide/UserGuideComponents/CanvaMapping";
import Users from "@/components/Users/Users";
import MainView from "@/Containers/MainView";
import DashboardContainer from "@/Containers/membership/Dashboard";
import UpdateMembershipDataView from "@/Containers/membership/UpdateData";
import UpdatePoliticalDataView from "@/Containers/political/UpdateData";

const router = createBrowserRouter([
  {
    path: "/",
    element: <MainView />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: <Navigate to="/membership/dashboard" replace /> },
      {
        path: "show-logs",
        element: <LogsDisplay />,
      },

      // --- USER GUIDE (shared between workflows) ---
      {
        path: "user-guide",
        element: <RoleWrapper allowedRoles={["membership", "political"]} />,
        children: [
          {
            element: <UserGuide />,
            children: [
              {
                index: true,
                element: <Navigate to="introduction-to-ada" replace />,
              },
              {
                path: "introduction-to-ada",
                element: <CanvaEmbed canvaId={canvaMappings.introToAda} />,
              },
              {
                path: "dashboard-guide",
                element: <CanvaEmbed canvaId={canvaMappings.dashboard} />,
              },
              {
                path: "update-data",
                element: <CanvaEmbed canvaId={canvaMappings.updateData} />,
              },
              {
                path: "catalist-only-docs",
                element: <CanvaEmbed canvaId={canvaMappings.catalistOnly} />,
              },
              {
                path: "download-data",
                element: <CanvaEmbed canvaId={canvaMappings.downloadData} />,
              },
              { path: "api-intro", element: <APIDocPage pageName="Intro" /> },
              {
                path: "api-token",
                element: <APIDocPage pageName="ObtainToken" />,
              },
              {
                path: "api-upload-file",
                element: <APIDocPage pageName="UploadData" />,
              },
              {
                path: "api-map-template",
                element: <APIDocPage pageName="MapTemplate" />,
              },
              {
                path: "api-process-file",
                element: <APIDocPage pageName="ProcessFile" />,
              },
              {
                path: "api-summary",
                element: <APIDocPage pageName="Summary" />,
              },
              {
                path: "api-confirm-file",
                element: <APIDocPage pageName="ConfirmFile" />,
              },
              {
                path: "api-catalist-options",
                element: <APIDocPage pageName="CatalistOptions" />,
              },
              {
                path: "template-dictionary",
                element: (
                  <CanvaEmbed canvaId={canvaMappings.fieldDefinitions} />
                ),
              },
              {
                path: "glossary",
                element: <CanvaEmbed canvaId={canvaMappings.glossary} />,
              },
              {
                path: "release-notes",
                element: <CanvaEmbed canvaId={canvaMappings.latestFeatures} />,
              },
              {
                path: "best-practices",
                element: <CanvaEmbed canvaId={canvaMappings.bestPractices} />,
              },
              {
                path: "political-data-upload",
                element: (
                  <CanvaEmbed canvaId={canvaMappings.politicalDataUpload} />
                ),
              },
              {
                path: "political-data-return",
                element: (
                  <CanvaEmbed canvaId={canvaMappings.politicalDataReturn} />
                ),
              },
            ],
          },
        ],
      },

      // --- MEMBERSHIP WORKFLOW ---
      {
        path: "membership",
        element: <RoleWrapper allowedRoles={["membership"]} />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: "dashboard", element: <DashboardContainer /> },
          {
            path: "update-data",
            element: <UpdateMembershipDataView />,
            children: [
              { index: true, element: <Navigate to="select-file" replace /> },
              { path: "select-file", element: <FileSelector /> },
              { path: "choose-template", element: <TemplatesTable /> },
              { path: "edit-template", element: <TemplateEditor /> },
              { path: "preview-file", element: <Preview /> },
              {
                path: "processing-confirmed",
                element: <ProcessConfirmation />,
              },
            ],
          },
          {
            path: "account",
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              { path: "profile", element: <Profile /> },
            ],
          },
          {
            path: "ieb",
            children: [{ index: true, element: <IEBPage /> }],
          },
          {
            path: "users",
            children: [{ index: true, element: <Users /> }],
          },
        ],
      },

      // --- POLITICAL WORKFLOW ---
      {
        path: "political",
        element: <RoleWrapper allowedRoles={["political"]} />,
        children: [
          { index: true, element: <Navigate to="update-data" replace /> },
          { path: "users", element: <Users /> },
          { path: "manage-events", element: <Events /> },
          { path: "dashboard", element: <VolunteerDashboard /> },
          {
            path: "update-data",
            element: <UpdatePoliticalDataView />,
            children: [
              { index: true, element: <Navigate to="select-file" replace /> },
              { path: "select-file", element: <PoliticalDataFileSelector /> },
              { path: "map-template", element: <EventDataTemplateEditor /> },
              { path: "confirmation", element: <Confirmation /> },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;
